import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormattedLocationString } from '@eventbrite/consumer-search.event-renderer';

import { SignalBadgeStatus } from '@/components/_shared/SignalBadge';
import { getDisplayTimezone } from '@/utils/dateTime';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import type { RelatedEvent, UrgencySignals, Venue } from '../../types';

// Type assertion helper for external library compatibility
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExternalVenue = any;

export interface FormattedEventData {
  id?: string;
  isFree: boolean;
  imageUrl: string;
  isOnlineEvent: boolean;
  isPromoted: boolean;
  locationString: string;
  formattedPriceString: string;
  name?: string;
  urgencySignals: UrgencySignals;
  url: string;
  venue: Venue | undefined;
  formattedEventDateTimeString: string;
  urgencySignalStatus: SignalBadgeStatus | null;
  priceString?: string;
}

// Helper functions to reduce complexity
const formatOnlineEventLocation = (
  formatMessage: (descriptor: { id: string; defaultMessage?: string; description?: string }) => string,
): string => {
  return formatMessage({
    id: 'Online event',
    defaultMessage: 'Online event',
    description: 'Label for online event location',
  });
};

const formatInPersonEventLocation = (locationStringFromHook: string): string => {
  return locationStringFromHook;
};

const formatDateTimeString = (
  startDate: string | undefined,
  startTime: string | undefined,
  timezone: string,
  locale: string,
  isOnlineEvent: boolean,
  attendeeTimezone?: string,
): string => {
  if (!startDate) return '';

  // Construct UTC date string from startDate and startTime
  const timeString = startTime ?? '00:00:00';
  const utcDateString = `${startDate}T${timeString}Z`;
  const dateObj = new Date(utcDateString);

  const targetTimezone = getDisplayTimezone({
    isOnlineEvent,
    eventTimezone: timezone,
    attendeeTimezone,
  });

  // Create formatters for each part to build the desired format: "Wed, 3 September • 11:30"
  const weekdayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    timeZone: targetTimezone,
  });

  const dayFormatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    timeZone: targetTimezone,
  });

  const monthFormatter = new Intl.DateTimeFormat(locale, {
    month: 'long',
    timeZone: targetTimezone,
  });

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
    timeZone: targetTimezone,
  });

  const timezoneFormatter = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone: targetTimezone,
  });

  // Get each part
  const weekday = weekdayFormatter.format(dateObj);
  const day = dayFormatter.format(dateObj);
  const month = monthFormatter.format(dateObj);
  const time = timeFormatter.format(dateObj);

  // Extract timezone abbreviation (e.g., "CET", "PDT", "EST")
  const timezoneFormatted = timezoneFormatter.format(dateObj);
  const timezoneRegex = /[A-Z]{2,5}/;
  const timezoneMatch = timezoneRegex.exec(timezoneFormatted);
  const timezoneShort = timezoneMatch ? timezoneMatch[0] : '';

  // Construct the desired format: "Wed, 3 September • 11:30 CET"
  const timezoneDisplay = timezoneShort ? ` ${timezoneShort}` : '';
  return `${weekday}, ${day} ${month} • ${time}${timezoneDisplay}`;
};

const getUrgencySignalStatus = (urgencySignals: UrgencySignals | undefined): SignalBadgeStatus | null => {
  if (!urgencySignals) return null;

  const trueUrgencySignals = Object.keys(urgencySignals).filter(
    (key) => urgencySignals[key as keyof UrgencySignals] === true,
  );

  return trueUrgencySignals.length > 0 ? (trueUrgencySignals[0] as SignalBadgeStatus) : null;
};

const formatPriceString = (
  isFree: boolean,
  formattedPriceString: string | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string | undefined => {
  if (!isFree && formattedPriceString) {
    return `${formatMessage({ id: 'Starts at' })} ${formattedPriceString}`;
  }
  return undefined;
};

const buildFormattedEventData = (
  relatedEvent: RelatedEvent,
  locationString: string,
  formattedEventDateTimeString: string,
  urgencySignalStatus: SignalBadgeStatus | null,
  priceString: string | undefined,
): FormattedEventData => ({
  id: relatedEvent.id,
  isFree: relatedEvent.isFree ?? false,
  imageUrl: relatedEvent.imageUrl ?? '',
  isOnlineEvent: relatedEvent.isOnlineEvent ?? false,
  isPromoted: relatedEvent.isPromoted ?? false,
  locationString,
  formattedPriceString: relatedEvent.formattedPriceString ?? '',
  name: relatedEvent.name,
  urgencySignals: relatedEvent.urgencySignals ?? {},
  url: relatedEvent.url ?? '#',
  venue: relatedEvent.venue,
  formattedEventDateTimeString,
  urgencySignalStatus,
  priceString,
});

export const useFormattedEventData = (relatedEvent: RelatedEvent | null): FormattedEventData | null => {
  const { formatMessage } = useIntl();
  const attendeeTimezone = useAttendeeTimezone(relatedEvent?.isOnlineEvent ?? false);

  // External hooks - must be called unconditionally
  const locationStringFromHook = useFormattedLocationString({
    venue: relatedEvent?.venue as ExternalVenue,
    style: 'full',
  });

  // Main formatted data
  const formattedData = useMemo((): FormattedEventData | null => {
    if (!relatedEvent) {
      return null;
    }

    const locationString = relatedEvent.isOnlineEvent
      ? formatOnlineEventLocation(formatMessage)
      : formatInPersonEventLocation(locationStringFromHook);

    const formattedEventDateTimeString = formatDateTimeString(
      relatedEvent.startDate,
      relatedEvent.startTime,
      relatedEvent.timezone ?? 'UTC',
      navigator.language,
      relatedEvent.isOnlineEvent ?? false,
      attendeeTimezone,
    );

    const urgencySignalStatus = getUrgencySignalStatus(relatedEvent.urgencySignals);

    const priceString = formatPriceString(
      relatedEvent.isFree ?? false,
      relatedEvent.formattedPriceString,
      formatMessage,
    );

    return buildFormattedEventData(
      relatedEvent,
      locationString,
      formattedEventDateTimeString,
      urgencySignalStatus,
      priceString,
    );
  }, [relatedEvent, formatMessage, locationStringFromHook, attendeeTimezone]);

  return formattedData;
};
